import React, { useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CardWrapper,
  CardHeader,
  CardTitle,
  GaugeWrapper,
  GaugeArcBox,
  GaugeCenterLabel,
  Description,
} from './style'
import AlertIconQuestion from '../AlertIconQuestion'
import { GAUGE_LEVELS, LEVEL_COLORS, LEVEL_LABELS, REMAINING_COLOR, TICKS, TICKS_DATA } from '../../constants'

const RADIAN = Math.PI / 180
const polarPoint = (cx, cy, radiusPx, angleDeg) => ({
  x: cx + radiusPx * Math.cos(-RADIAN * angleDeg),
  y: cy + radiusPx * Math.sin(-RADIAN * angleDeg),
})

const renderTicks = (intl) => (props) => {
  const { cx, cy, outerRadius } = props
  const unit = outerRadius / 100

  return (
    <g>
      {TICKS.map(({ key, angle, labelR, lineFromR, lineToR, anchor }) => {
        const label = polarPoint(cx, cy, unit * labelR, angle)
        const lineStart = polarPoint(cx, cy, unit * lineFromR, angle)
        const lineEnd = polarPoint(cx, cy, unit * lineToR, angle)

        return (
          <g key={key}>
            <line x1={lineStart.x} y1={lineStart.y} x2={lineEnd.x} y2={lineEnd.y} stroke="#d1d5db" strokeWidth={1} />
            <text x={label.x} y={label.y} textAnchor={anchor} dominantBaseline="middle" fontSize={11} fill="#9ca3af">
              {intl.formatMessage(LEVEL_LABELS[key])}
            </text>
          </g>
        )
      })}
    </g>
  )
}

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props

  return (
    <g>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 2} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  )
}

const ResponseGaugeCard = ({
  titleId,
  titleDefaultMessage,
  helpTextId,
  helpTextDefaultMessage,
  level,
  descriptionId,
  descriptionDefaultMessage,
}) => {
  const intl = useIntl()
  const [activeIndex, setActiveIndex] = useState(-1)

  const levelIndex = GAUGE_LEVELS.indexOf(level)
  const percent = (levelIndex / (GAUGE_LEVELS.length - 1)) * 100
  const color = LEVEL_COLORS[level]
  const levelLabel = intl.formatMessage(LEVEL_LABELS[level])

  const remainingLabel = intl.formatMessage({ id: 'monitoringGaugeRemaining', defaultMessage: 'Remaining' })
  const chartData = [
    { key: 'filled', value: percent, fill: color, label: levelLabel },
    { key: 'remaining', value: 100 - percent, fill: REMAINING_COLOR, label: remainingLabel },
  ]

  const handleSectorEnter = (_, index) => setActiveIndex(index)
  const handleSectorLeave = () => setActiveIndex(-1)

  return (
    <CardWrapper>
      <CardHeader>
        <CardTitle>
          <FormattedMessage id={titleId} defaultMessage={titleDefaultMessage} />
        </CardTitle>
        {(helpTextId || helpTextDefaultMessage) && (
          <AlertIconQuestion title={helpTextId ? <FormattedMessage id={helpTextId} defaultMessage={helpTextDefaultMessage} /> : helpTextDefaultMessage} />
        )}
      </CardHeader>

      <GaugeWrapper>
        <GaugeArcBox>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ left: 35, right: 35, top: 15, bottom: 5 }} style={{ overflow: 'visible' }}>
              <Pie
                data={TICKS_DATA}
                dataKey="value"
                cx="50%"
                cy="100%"
                startAngle={180}
                endAngle={0}
                outerRadius="100%"
                isAnimationActive={false}
                labelLine={false}
                label={renderTicks(intl)}
              >
                <Cell fill="none" stroke="none" />
              </Pie>

              <Pie
                data={chartData}
                dataKey="value"
                nameKey="key"
                cx="50%"
                cy="100%"
                startAngle={180}
                endAngle={0}
                innerRadius="130%"
                outerRadius="160%"
                cornerRadius={6}
                stroke="none"
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={handleSectorEnter}
                onMouseLeave={handleSectorLeave}
                isAnimationActive={false}
              />
            </PieChart>
          </ResponsiveContainer>

          <GaugeCenterLabel>{levelLabel}</GaugeCenterLabel>
        </GaugeArcBox>
      </GaugeWrapper>

      {descriptionId && (
        <Description>
          <FormattedMessage id={descriptionId} defaultMessage={descriptionDefaultMessage} />
        </Description>
      )}
    </CardWrapper>
  )
}

export default ResponseGaugeCard